import { AxiosInstance } from 'axios'
import { Alpha3Code } from 'i18n-iso-countries'

import { DisbursementStatusAndTotal } from '@percent/admin-dashboard/app/disbursements/disbursementDetails/statusOverview/StatusOverview.types'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'

export type disbursementsServiceCapabilities = {
  percentClient: AxiosInstance
}

export type GetDisbursementBatchesListProps = {
  pageSize?: number
  cursor?: string
  search?: string | null
  createdAtFrom?: string | null
  createdAtTo?: string | null
  endDateFrom?: string | null
  endDateTo?: string | null
  sort?: string | null
  direction?: string | null
  foundationId?: string | null
  allDisbursementsWithStatus?: (string | null)[] | null
  anyDisbursementsWithStatus?: (string | null)[] | null
  allDisbursementsWithTypes?: (string | null)[] | null
  anyDisbursementsWithTypes?: (string | null)[] | null
}

/** Mirrors the API's `sort` enum; the endpoint rejects anything else with a 400. */
export const DisbursementBatchesSortField = {
  ID: 'id',
  CREATED_AT: 'createdAt',
  END_DATE: 'endDate'
} as const
export type DisbursementBatchesSortField =
  typeof DisbursementBatchesSortField[keyof typeof DisbursementBatchesSortField]

export const DisbursementBatchesSortDirection = {
  ASC: 'ASC',
  DESC: 'DESC'
} as const
export type DisbursementBatchesSortDirection =
  typeof DisbursementBatchesSortDirection[keyof typeof DisbursementBatchesSortDirection]

/** Query params come from the URL, so both are narrowed before being sent to the strict endpoint. */
export const isDisbursementBatchesSortField = (value?: string | null): value is DisbursementBatchesSortField =>
  Object.values(DisbursementBatchesSortField).includes(value as DisbursementBatchesSortField)

export const isDisbursementBatchesSortDirection = (value?: string | null): value is DisbursementBatchesSortDirection =>
  Object.values(DisbursementBatchesSortDirection).includes(value as DisbursementBatchesSortDirection)

/**
 * The date params are URL-supplied too, so they get the same treatment. The picker writes a full
 * offset date and a plain day is accepted so a hand-written or shared link still works, but the
 * leading `YYYY-MM-DD` is required: dayJS alone parses `2026` happily, and it rolls `2026-13-45`
 * over into a real 2027 date rather than rejecting it — hence the round-trip rather than isValid().
 */
const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})([T ].*)?$/

export const isDisbursementBatchesDate = (value?: string | null): value is string => {
  const day = value?.match(ISO_DATE_PREFIX)?.[1]

  return !!day && dayJS(day).format('YYYY-MM-DD') === day
}

export type GetDisbursementsInBatchListProps = {
  pageSize?: number
  cursor?: string
  organisationId?: string[]
  status?: string[]
  beneficiaryStatus?: string[]
  sort?: string
  direction?: string
}

export type DetailedDisbursementsCount = {
  type: DisbursementPaymentMethod
  status: DisbursementStatusAndTotal
  count: number
}

export const DisbursementBatchStatus = {
  APPROVAL_REQUIRED: 'approval_required',
  TRUSTEE_APPROVAL_REQUIRED: 'trustee_approval_required',
  AGENT_APPROVAL_REQUIRED: 'agent_approval_required',
  MANAGER_APPROVAL_REQUIRED: 'manager_approval_required',
  APPROVED: 'approved',
  PAID: 'paid',
  CANCELLED: 'cancelled'
} as const
export type DisbursementBatchStatus = typeof DisbursementBatchStatus[keyof typeof DisbursementBatchStatus]

type DisbursementBatchAmountsByKind = {
  approvalRequired: number
  trusteeApprovalRequired: number
  agentApprovalRequired: number
  managerApprovalRequired: number
  approved: number
  paymentInitiated: number
  inTransit: number
  paid: number
  cancelled: number
  failed: number
  returned: number
}

export type DisbursementBatchCounts = DisbursementBatchAmountsByKind & {
  total: number
}

export type DisbursementBatchTotals = DisbursementBatchAmountsByKind & {
  total: number
  currencyCode: string
}

export type DisbursementBatch = {
  id: string
  createdAt: string
  foundationId: string
  createdBy: string
  endDate: string
  name?: string
  status?: DisbursementBatchStatus
  counts: DisbursementBatchCounts
  totals: DisbursementBatchTotals
  organisationCount: number
  detailedCounts: DetailedDisbursementsCount[]
  allBeneficiariesConfirmed: boolean
}

export enum DisbursementType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
  CHEQUE = 'cheque'
}

export type DisbursementPaymentMethod = 'bank_transfer' | 'card' | 'cheque'

export type DisbursementPaymentMethodDetails = Readonly<{
  id: string
  method: DisbursementPaymentMethod
  bankAccountDetails?: {
    version: string
    holderName: string
    wireTransferDetails: Record<string, string>
  } | null
  paymentUrl?: string | null
}> | null

export type CreateDisbursementBatchProps = {
  type: DisbursementType
  paymentMethodTypes?: DisbursementPaymentMethod[]
  foundationId: string
  endDate: string
  batchAggregationType: 'standalone' | 'bundled'
  partnerId?: string
  name?: string
}

export type GetDisbursementBatchDetailsProps = {
  id: string
}

export type UpdateDisbursementBatchProps = {
  id: string
  /** An empty string clears the name — it is optional on the batch. */
  name: string
}

export type DisbursementBatchProgressBucket = {
  count: number
  amount: number
}

export type DisbursementBatchProgressDisbursementBuckets = {
  approval_required: DisbursementBatchProgressBucket
  trustee_approval_required: DisbursementBatchProgressBucket
  agent_approval_required: DisbursementBatchProgressBucket
  manager_approval_required: DisbursementBatchProgressBucket
  approved: DisbursementBatchProgressBucket
  in_transit: DisbursementBatchProgressBucket
  payment_initiated: DisbursementBatchProgressBucket
  paid: DisbursementBatchProgressBucket
  cancelled: DisbursementBatchProgressBucket
  failed: DisbursementBatchProgressBucket
  returned: DisbursementBatchProgressBucket
}

export type DisbursementBatchProgressPaymentBuckets = {
  PAYMENT_INITIATED: DisbursementBatchProgressBucket
  COMPLETED: DisbursementBatchProgressBucket
  FAILED: DisbursementBatchProgressBucket
  RETURNED: DisbursementBatchProgressBucket
}

export type DisbursementBatchProgress = {
  currencyCode: string
  disbursements: DisbursementBatchProgressDisbursementBuckets
  payments: DisbursementBatchProgressPaymentBuckets
}

export type GetDisbursementBatchProgressProps = {
  id: string
}

export enum BeneficiaryStatus {
  ACTIVE = 'ACTIVE',
  PROCESSING = 'PROCESSING',
  OUTDATED = 'OUTDATED'
}

export const BENEFICIARY_NO_BENEFICIARY = 'NO_BENEFICIARY' as const

export type DisbursementBeneficiary = {
  id: string
  status: BeneficiaryStatus
  lastUsedAt?: string
  approvedAt?: string
  outdatedAt?: string
}

export enum DisbursementStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PENDING_TRUSTEE_APPROVAL = 'PENDING_TRUSTEE_APPROVAL',
  PENDING_AGENT_APPROVAL = 'PENDING_AGENT_APPROVAL',
  PENDING_MANAGER_APPROVAL = 'PENDING_MANAGER_APPROVAL',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PAYMENT_INITIATED = 'PAYMENT_INITIATED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  SENT = 'SENT',
  RETURNED = 'RETURNED',
  FAILED = 'FAILED'
}

export type Disbursement = {
  id: string
  approvedAt?: string
  approvedBy?: string
  trusteeApprovedAt?: string
  agentApprovedAt?: string
  managerApprovedAt?: string
  paidAt?: string
  paidBy?: string
  createdAt: string
  updatedAt?: string
  createdBy: string
  disbursementBatchId: string
  foundationAmount: number
  foundationCurrencyCode: string
  foundationId: string
  status: DisbursementStatus
  organisationId: string
  paidCurrencyCode?: string
  amount: number
  paidAmount?: number
  actualOrganisationCurrencyCode: string
  actualOrganisationAmount: number
  type: DisbursementType
  partnerId?: string
  paymentCount?: number
  beneficiary?: DisbursementBeneficiary
  bankDetailsReviewId?: string
  paymentMethod?: DisbursementPaymentMethodDetails
}

export type DisbursementEnhanced = Disbursement & {
  organisationName: string
  organisationCountryCode: Alpha3Code
  partnerName?: string
  hasActivePaymentMethod?: boolean
}

export type MarkDisbursementAsPaidProps = {
  id: string
  paidAt: string
  paidAmount: number
  paidCurrency: string
}

export type GetDisbursementDonationsProps = {
  pageSize?: number
  cursor?: string
  disbursementId: string
}

export type LogReturnedPaymentProps = {
  disbursementId: string
  reason?: string
}

export type BeneficiaryDetails = {
  disbursementId: string
  beneficiaryId?: string
  wireTransferDetails: {
    accountNumber?: string
    bankCode?: string
  }
  organisationId: string
  organisationCountry: string
  approvedAt?: string
  approvedBy?: string
  lastUsedAt?: string
  outdatedAt?: string
  outdatedBy?: string
  createdAt?: string
}

export type BulkMarkAsPaidSkippedItem = {
  disbursementId: string
  reason: string
}

export type BulkMarkAsPaidDryRunResponse = {
  data: {
    succeeded: 0
    skipped: BulkMarkAsPaidSkippedItem[]
    validDisbursementIds: string[]
  }
  object: string
}

export type BulkMarkAsPaidConfirmResponse = {
  data: {
    succeeded: number
    skipped: BulkMarkAsPaidSkippedItem[]
  }
  object: string
}

export type BulkMarkAsPaidResponse = BulkMarkAsPaidDryRunResponse | BulkMarkAsPaidConfirmResponse

export type BulkDisbursementsFailure = {
  disbursementId: string
  reason: string
}

export type BulkDisbursementsResponse = {
  succeeded: string[]
  failed: BulkDisbursementsFailure[]
}

export type DisbursementBatchCancellationFailure = {
  disbursementId: string
  error: string
}

export type DisbursementBatchWithCancellationFailures = DisbursementBatch & {
  cancellationFailures: DisbursementBatchCancellationFailure[]
}
